/**
 * Client-side <Link> navigations do not hit next.config.js redirects (data fetches use /_next/data/...).
 * Returning { redirect } from getStaticProps fixes in-app navigation without middleware.
 *
 * Next.js does not allow redirect from getStaticProps while prerendering a path from getStaticPaths
 * at build time. Omit these slugs from getStaticPaths; with fallback: "blocking", the first request
 * runs getStaticProps on-demand (still static generation, not getServerSideProps) and may redirect.
 *
 * Keep in sync with redirects() in next.config.js for /blog/* sources.
 */

/** @type {Record<string, string>} params.slug → destination pathname (trailing slash matches site) */
export const legacyBlogPostSlugRedirects = {
  "aws-monitoring-best-practices": "/blog/aws-monitoring/best-practices/",
  "aws-profiler": "/blog/aws-monitoring/aws-profiler/",
  "focus-on-a-core-product": "/blog/",
  "how-to-use-synthetic-monitoring": "/blog/what-is-synthetic-monitoring/",
  "kubernetes-monitoring-tools": "/blog/kubernetes-monitoring/tools/",
  "observability-best-practices": "/blog/observability/best-practices/",
  "observability-tools": "/blog/observability/tools/",
  "observability-trends": "/blog/observability/trends/",
  "top-10-observability-trends-in-2024": "/blog/observability/trends/",
  "top-observability-trends": "/blog/observability/trends/",
  "ways-to-reduce-it-costs-with-observability":
    "/blog/observability/ways-to-reduce-it-costs-with-observability/",
  "observability-ways-to-reduce-it-costs-with-observability":
    "/blog/observability/ways-to-reduce-it-costs-with-observability/",
  "what-is-alert-fatigue-a-devops-guide-on-how-to-avoid-it": "/blog/what-is-alert-fatigue/",
  "january-2025-product-updates": "/product-updates/jan-2025/",
  "product-update-feb-2023": "/product-updates/feb-2023/",
  "product-updates-april-2023": "/product-updates/april-2023/",
  "product-updates-august-2023": "/product-updates/august-2023/",
  "product-updates-december-2024": "/product-updates/dec-2024/",
  "product-updates-jan-2023": "/product-updates/jan-2023/",
  "product-updates-july-2023": "/product-updates/july-2023/",
  "product-updates-june-2023": "/product-updates/june-2023/",
  "product-updates-march-2023": "/product-updates/march-2023/",
  "product-updates-may-2023": "/product-updates/may-2023/",
  "product-updates-november-2024": "/product-updates/nov-2024/",
  "product-updates-october-2024": "/product-updates/oct-2024/",
  "product-updates-september-2024": "/product-updates/sep-2024/",
};

/** @type {Record<string, string>} params.slug on pages/blog/category/[slug] */
export const legacyBlogCategorySlugRedirects = {
  "product-updates": "/product-updates/",
};

export function getLegacyBlogPostRedirectDestination(slug) {
  if (!slug || typeof slug !== "string") return null;
  return legacyBlogPostSlugRedirects[slug] ?? null;
}

export function getLegacyBlogCategoryRedirectDestination(slug) {
  if (!slug || typeof slug !== "string") return null;
  return legacyBlogCategorySlugRedirects[slug] ?? null;
}

/** Exclude from getStaticPaths so build-time prerender does not hit redirect (see file header). */
export function isLegacyBlogPostSlug(slug) {
  if (!slug || typeof slug !== "string") return false;
  return Object.prototype.hasOwnProperty.call(legacyBlogPostSlugRedirects, slug);
}

/** Exclude from getStaticPaths so build-time prerender does not hit redirect (see file header). */
export function isLegacyBlogCategorySlug(slug) {
  if (!slug || typeof slug !== "string") return false;
  return Object.prototype.hasOwnProperty.call(legacyBlogCategorySlugRedirects, slug);
}
